/** Shared, bounded ERP read cache. Business writes invalidate reads and snapshots. */
const RETAIN_MS = 5 * 60_000;
const MAX_ENTRIES = 100;
type Entry = { value: unknown; savedAt: number };
type Pending = { controller: AbortController; promise: Promise<unknown>; users: number };
const entries = new Map<string, Entry>();
const pending = new Map<string, Pending>();
const freshSignals = new WeakSet<AbortSignal>();
let scope: string | null = null;
let invalidationRevision=0;
const revisions=new Map<string,number>();
/** Includes mounted readers whose first response has not populated the cache yet. */
export function queryRevision(key:string) {
    if(!revisions.has(key))revisions.set(key,invalidationRevision);
    const value=revisions.get(key)!;
    if(revisions.size>1000)revisions.delete(revisions.keys().next().value!);
    return value;
}

function abortError() { return new DOMException('Request cancelled', 'AbortError'); }
export function readSessionKey() {
    return JSON.stringify(['supplier_portal_session','authToken','isAuthenticated','userRole'].map(key=>localStorage.getItem(key)));
}
function ensureScope() {
    const next = readSessionKey();
    if (next !== scope) { clearQueryCache(); scope = next; }
}
export function invalidateQueries(matches: (key: string) => boolean, cancelPending = true) {
    const revision=++invalidationRevision;
    for(const key of revisions.keys())if(matches(key))revisions.set(key,revision);
    for (const key of Array.from(entries.keys())) if (matches(key)) entries.delete(key);
    for (const [key, request] of Array.from(pending.entries())) {
        if (matches(key)) { pending.delete(key); if(cancelPending)request.controller.abort(); }
    }
}
export function clearQueryCache(fragment?: string) {
    invalidateQueries(key => !fragment || key.includes(fragment));
}
/** Presentation preferences expire warm snapshots without discarding business reads. */
export function expireQuerySnapshots(matches: (key: string) => boolean) {
    for (const key of entries.keys()) if (matches(key)) entries.delete(key);
}
export function peekQueryState<T>(key: string, maxAge = RETAIN_MS): { value: T; updatedAt: number } | undefined {
    ensureScope();
    const entry = entries.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.savedAt >= RETAIN_MS) { entries.delete(key); return undefined; }
    if (Date.now() - entry.savedAt >= maxAge) return undefined;
    entries.delete(key); entries.set(key, entry);
    return { value: entry.value as T, updatedAt: entry.savedAt };
}
export function peekQuery<T>(key: string, maxAge = RETAIN_MS): T | undefined {
    return peekQueryState<T>(key, maxAge)?.value;
}
export function putQuery<T>(key: string, value: T) {
    ensureScope();
    entries.delete(key); entries.set(key, { value, savedAt: Date.now() });
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
}

/** Subscribers share a GET; leaving one page does not cancel another subscriber. */
export function readQuery<T>(key: string, load: (signal: AbortSignal) => Promise<T>,
    options: { signal?: AbortSignal; staleTime?: number } = {}): Promise<T> {
    ensureScope();
    if (options.signal?.aborted) return Promise.reject(abortError());
    const forceFresh=options.staleTime===0 || !!options.signal && freshSignals.has(options.signal);
    const cached = peekQuery<T>(key, forceFresh ? 0 : options.staleTime ?? 30_000);
    if (cached !== undefined) return Promise.resolve(cached);
    let request = pending.get(key);
    if (!request) {
        const controller = new AbortController();
        if(forceFresh)freshSignals.add(controller.signal);
        const created: Pending = { controller, users: 0, promise: Promise.resolve() };
        let timedOut = false;
        const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 30_000);
        const aborted = new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => reject(abortError()), {once:true}));
        created.promise = Promise.race([aborted, Promise.resolve().then(() => load(controller.signal))]).then(value => {
            ensureScope();
            if (controller.signal.aborted || pending.get(key) !== created) throw abortError();
            putQuery(key, value);
            return value;
        }).catch(error => {
            if (timedOut) throw new Error('请求超时，请重试');
            throw error;
        }).finally(() => {
            clearTimeout(timer);
            if (pending.get(key) === created) pending.delete(key);
        });
        pending.set(key, created);
        request = created;
    }
    const shared = request;
    shared.users++;
    return new Promise<T>((resolve, reject) => {
        let done = false;
        const finish = (action: () => void) => {
            if (done) return;
            done = true; options.signal?.removeEventListener('abort', cancelled);
            shared.users--;
            if (shared.users === 0) {
                if(pending.get(key) === shared)pending.delete(key); shared.controller.abort();
            }
            action();
        };
        const cancelled = () => finish(() => reject(abortError()));
        options.signal?.addEventListener('abort', cancelled, { once: true });
        shared.promise.then(value => finish(() => resolve(value as T)), error => finish(() => reject(error)));
    });
}
